import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { PLAN_ENABLEMENT_FLAG_NAMES_LIST } from '../config/planEnablement.config';
import {
  createPlanProductEnablementChecker,
  getMergedPlanEnablement,
  hasMergedPlanEnablementData,
  isFeatureEnabledForProducts,
} from '../enablement';
import type { EntitlementsState } from '../state/entitlementsSlice';
import { selectEntitlementsOperation } from '../state/entitlementsSlice';
import {
  getAssetProductCodesFromEnablementsData,
  isFeatureEnabledForOrgProductEntitlements,
  isFeatureEntitledFromEnablementsData,
  isFeatureEntitledFromOrgProductEntitlements,
  isProductEntitledFromEnablementsData,
  isProductEntitledFromOrgProductEntitlements,
} from '../utils/entitlements.utils';

/**
 * Org- and asset-scoped entitlement checks merged with plan enablement flags (**P**).
 * Pairwise `(product, feature)` gating; **empty P ⇒ false** (strict — same as `hasFeatureAccess` with `getFlag`).
 *
 * `isReady` is `true` once the entitlements fetch has settled (succeeded or failed).
 * `isLoading` is `true` while the fetch is in-flight.
 * Consumers should wait for `isReady` before trusting `hasFeature`/`hasProduct` return values
 * to avoid false negatives during the initial load.
 */
export const useEntitlements = (): {
  hasFeature: (
    featureCode: string,
    assetId?: string,
    assetType?: 'vehicle' | 'peripheral'
  ) => boolean;
  /** Returns true when any asset of the requested type has plan-enabled feature access. */
  hasFeatureForAnyAsset: (
    featureCode: string,
    assetType?: 'vehicle' | 'peripheral'
  ) => boolean;
  hasProduct: (
    productCode: string,
    assetId?: string,
    assetType?: 'vehicle' | 'peripheral'
  ) => boolean;
  isReady: boolean;
  isLoading: boolean;
} => {
  const entitlements = useSelector(
    (state: { entitlements: EntitlementsState }) => state.entitlements
  );
  const entitlementsOperation = useSelector(selectEntitlementsOperation);
  const { getFlag } = useFeatureFlags();
  const mergedPlanEnablement = useMemo(
    () => getMergedPlanEnablement(getFlag, PLAN_ENABLEMENT_FLAG_NAMES_LIST),
    [getFlag]
  );
  const isProductEnabledByPlanFlags = useMemo(
    () =>
      createPlanProductEnablementChecker(
        getFlag,
        PLAN_ENABLEMENT_FLAG_NAMES_LIST
      ),
    [getFlag]
  );

  const hasFeature = (
    featureCode: string,
    assetId?: string,
    assetType?: 'vehicle' | 'peripheral'
  ) => {
    if (!entitlements) {
      return false;
    }
    let hasEntitlementFromData = false;
    let assetProductCodes: string[] = [];
    const enablements = entitlements.rawEntitlements?.enablements;
    // default to org feature check if assetId is invalid
    if (!assetId || typeof assetId !== 'string') {
      hasEntitlementFromData = isFeatureEntitledFromOrgProductEntitlements(
        featureCode,
        entitlements.rawEntitlements?.entitlements
      );
    } else if (!assetType || assetType === 'vehicle') {
      hasEntitlementFromData = isFeatureEntitledFromEnablementsData(
        featureCode,
        assetId,
        enablements,
        'vehicle'
      );
      assetProductCodes = getAssetProductCodesFromEnablementsData(
        enablements,
        assetId,
        'vehicle'
      );
    } else if (assetType === 'peripheral') {
      hasEntitlementFromData = isFeatureEntitledFromEnablementsData(
        featureCode,
        assetId,
        enablements,
        'peripheral'
      );
      assetProductCodes = getAssetProductCodesFromEnablementsData(
        enablements,
        assetId,
        'peripheral'
      );
    }

    const isPlanEnabled =
      !assetId || typeof assetId !== 'string'
        ? isFeatureEnabledForOrgProductEntitlements(
            mergedPlanEnablement,
            featureCode,
            entitlements.rawEntitlements?.entitlements
          )
        : assetProductCodes.length > 0
          ? isFeatureEnabledForProducts(
              mergedPlanEnablement,
              featureCode,
              assetProductCodes
            )
          : false;
    return hasEntitlementFromData && isPlanEnabled;
  };
  const hasFeatureForAnyAsset = useCallback(
    (
      featureCode: string,
      assetType: 'vehicle' | 'peripheral' = 'vehicle'
    ): boolean => {
      if (!entitlements || !hasMergedPlanEnablementData(mergedPlanEnablement)) {
        return false;
      }

      const accessor = assetType === 'vehicle' ? 'VINS' : 'PERIPHERALS';
      const enablements = entitlements.rawEntitlements?.enablements;
      const assetFeatures = enablements?.features?.[accessor] ?? {};

      return Object.entries(assetFeatures).some(([assetId, featureCodes]) => {
        if (!featureCodes.includes(featureCode)) {
          return false;
        }

        const assetProductCodes = getAssetProductCodesFromEnablementsData(
          enablements,
          assetId,
          assetType
        );

        return (
          assetProductCodes.length > 0 &&
          isFeatureEnabledForProducts(
            mergedPlanEnablement,
            featureCode,
            assetProductCodes
          )
        );
      });
    },
    [entitlements, mergedPlanEnablement]
  );
  const hasProduct = (
    productCode: string,
    assetId?: string,
    assetType?: 'vehicle' | 'peripheral'
  ) => {
    if (!entitlements) {
      return false;
    }
    let hasEntitlementFromData = false;
    const enablements = entitlements.rawEntitlements?.enablements;
    // default to org product check if assetId is invalid
    if (!assetId || typeof assetId !== 'string') {
      hasEntitlementFromData = isProductEntitledFromOrgProductEntitlements(
        productCode,
        entitlements.rawEntitlements?.entitlements
      );
    } else if (!assetType || assetType === 'vehicle') {
      hasEntitlementFromData = isProductEntitledFromEnablementsData(
        productCode,
        assetId,
        enablements,
        'vehicle'
      );
    } else if (assetType === 'peripheral') {
      hasEntitlementFromData = isProductEntitledFromEnablementsData(
        productCode,
        assetId,
        enablements,
        'peripheral'
      );
    }
    return hasEntitlementFromData && isProductEnabledByPlanFlags(productCode);
  };
  return {
    hasFeature,
    hasFeatureForAnyAsset,
    hasProduct,
    isReady:
      entitlementsOperation === 'succeeded' ||
      entitlementsOperation === 'failed',
    isLoading: entitlementsOperation === 'loading',
  };
};
